import axiosApi from "@/services/axios/axios";

import {
  ForgotPasswordParams,
  loginParams,
  signupParams,
  resetPasswordParams,
  ChangePasswordParams,
  UpdateUserParams,
} from "@/types/authentication";

import { AuthType } from "@/types/apiResposne";
import { ErrorHelper } from "@/utils/errorHelper";
/**
 * logs the user in .
 *
 * @param {loginParams}  params- The user credentials.

 * @returns {Promise<any>} - Returns a promise resolving with the response data if valid.
 * @throws {string} - Throws an error message if the request fails.
 */
export const loginUser = async (
  params: loginParams,
): Promise<AuthType.LoginSuccessResponse> => {
  try {
    const response = await axiosApi.post<AuthType.LoginSuccessResponse>(
      "/auth/login",
      params,
    );
    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};

/**
 * Creates a new user .
 *
 * @param {signupParams}  params- The new user details.
 * @param {string} signupCode - .The code received by the specific user
 * @returns {Promise<any>} - Returns a promise resolving with the response data if valid.
 * @throws {string} - Throws an error message if the request fails.
 */

export const signupUser = async (
  params: signupParams,
  signupCode: string,
): Promise<AuthType.SignupResponse> => {
  try {
    const response = await axiosApi.post<AuthType.SignupResponse>(
      `/organization/invite/${signupCode}/signup`,
      params,
    );
    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};

/**
 * Sends a password reset confirmation request to the server.
 *
 * @param {changePasswordParams} params - The new password details.
 * @param {string} token - The token sent to the user's email for verification.
 * @param {string} uidb64 - The base64 encoded user ID.
 * @returns {Promise<any>} - Returns a promise resolving with the response data.
 * @throws {string} - Throws an error message if the request fails.
 */

export const resetPasswordApi = async (
  params: resetPasswordParams,
  token: string,
  uidb64: string,
): Promise<AuthType.ResetPasswordResponse> => {
  try {
    const response = await axiosApi.post<AuthType.ResetPasswordResponse>(
      `/auth/password-reset-confirm/${uidb64}/${token}`,
      params,
    );
    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};

export const verifySignupTokenApi = async (
  token: string,
): Promise<AuthType.SignupResponse> => {
  try {
    const response = await axiosApi.get<AuthType.SignupResponse>(
      `/organization/invite/verify/${token}`,
    );
    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};
/**
 * Verifies the validity of the password reset token and user ID.
 *
 * @param {string} uidb64 - The base64 encoded user ID.
 * @param {string} token - The token to verify.
 * @returns {Promise<any>} - Returns a promise resolving with the response data if valid.
 * @throws {string} - Throws an error message if the request fails.
 */

export const verifyChangePasswordApi = async (
  uidb64: string,
  token: string,
): Promise<AuthType.ResetPasswordResponse> => {
  try {
    const response = await axiosApi.get<AuthType.ResetPasswordResponse>(
      `/auth/password-reset/verify/${uidb64}/${token}`,
    );
    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};

/**
 * Redirects the user to login page and blacklists the refresh token.
 *
 * @param {string} refreshToken - The token to blacklist.
 * @returns {Promise<any>} - Returns a promise resolving with the response data if valid.

 */

export const logoutUser = async (refreshToken: string) => {
  try {
    const response = await axiosApi.post("/auth/logout", {
      refresh: refreshToken,
    });
    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};

/**
 * Redirects the user to login page and sends the link to create new password to user's mail
 *
 * @param {ForgotPasswordParams} params - The email of the user
 * @returns {Promise<any>} - Returns a promise resolving with the response data if valid.
 * @throws {string} - Throws an error message if the request fails.
 */
export const forgotPassword = async (
  params: ForgotPasswordParams,
): Promise<AuthType.ForgotPasswordResponse> => {
  try {
    const response = await axiosApi.post<AuthType.ForgotPasswordResponse>(
      "/auth/password-reset",
      params,
    );
    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};

export const userInfo = async (): Promise<AuthType.UserInfoResponse> => {
  try {
    const response = await axiosApi.get<AuthType.UserInfoResponse>("userinfo");
    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};

export const updateUserApi = async (
  id: number,
  params: UpdateUserParams,
): Promise<AuthType.UpdateUserResponse> => {
  try {
    const response = await axiosApi.put<AuthType.UpdateUserResponse>(
      `auth/users/${id}`,
      params,
    );
    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};

export const changePasswordApi = async (
  params: ChangePasswordParams,
): Promise<AuthType.ChangePasswordSuccess> => {
  try {
    const response = await axiosApi.post<AuthType.ChangePasswordSuccess>(
      "/auth/password-change",
      params,
    );

    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};

/**
 * Redirects the user to login page and blacklists the refresh token.
 *
 * @param {string} refreshToken - The token of the user.
 * @returns {Promise<any>} - Returns a promise resolving with the response data if valid.

 */

export const accessTokenRefresh = async (refreshToken: string) => {
  try {
    const response = await axiosApi.post("/auth/token/refresh", {
      refresh: refreshToken,
    });
    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};
